import { type Paginated, MAX_PAGE_SIZE } from '#shared/types';
import { api } from '#client/lib/api';
import { useKeyedRequest } from './useKeyedRequest';

/** A value that can be sent as a query parameter. */
type QueryValue = string | number | undefined;

interface AllRecords<T> {
  items: T[];
  loading: boolean;
  error?: string | undefined;
  refresh: () => void;
}

/**
 * Every record of a list endpoint, for a picker.
 *
 * Reads page after page until the count is reached, because a single page is
 * capped at `MAX_PAGE_SIZE` — and a picker that silently stops at the hundredth
 * lab test is one where the hundred-and-first can never be booked.
 */
export function useAllRecords<T>({
  path,
  extraQuery,
}: {
  path: string;
  extraQuery?: Record<string, QueryValue> | undefined;
}): AllRecords<T> {
  const request = useKeyedRequest<T[]>(
    JSON.stringify({ path, extraQuery: extraQuery ?? {} }),
    (signal) => loadEveryPage<T>(path, { extraQuery, signal }),
    'Could not load records',
  );

  return {
    items: request.data ?? [],
    loading: request.loading,
    error: request.error,
    refresh: request.refresh,
  };
}

async function loadEveryPage<T>(
  path: string,
  {
    extraQuery,
    signal,
  }: { extraQuery: Record<string, QueryValue> | undefined; signal: AbortSignal },
): Promise<T[]> {
  const items: T[] = [];
  for (let page = 1; ; page += 1) {
    const result: Paginated<T> = await api.list<T>(
      path,
      { ...extraQuery, page, limit: MAX_PAGE_SIZE },
      signal,
    );
    items.push(...result.items);
    if (result.items.length === 0 || items.length >= result.count) return items;
  }
}
